import type { ReactNode, Ref } from "react"
import { BreadcrumbComponent } from "@/components/ui/breadcrumb"
import { cn } from "@/lib/utils"

interface ToolPageShellProps {
  /** Current page name, the last breadcrumb item (e.g. "Mowing Height Calculator"). */
  breadcrumbLabel: string
  children: ReactNode
  /** Rendered on the page ground but outside the column (modals, portals). */
  after?: ReactNode
  /** Ref on the page ground, e.g. the `useToolStarted` observer ref. */
  rootRef?: Ref<HTMLDivElement>
  className?: string
}

/**
 * Page ground + content column + breadcrumb for a /tools/* calculator island.
 *
 * The column matches ToolContentFooter and ToolPillarFooter (max-w-5xl with the
 * same gutters), so the island and the server-rendered footer share one left
 * edge. The breadcrumb never emits JSON-LD: every tool layout already renders
 * ToolArticleSchema, which owns the BreadcrumbList. Nothing here animates on
 * load, so the island is visible in its SSR HTML.
 */
export function ToolPageShell({ breadcrumbLabel, children, after, rootRef, className }: ToolPageShellProps) {
  return (
    <div ref={rootRef} className="min-h-screen bg-linear-to-br from-green-50 via-white to-gray-50">
      <div className={cn("mx-auto w-full max-w-5xl px-4 pt-4 pb-8 md:px-6 md:pt-8 md:pb-16 lg:px-8", className)}>
        <BreadcrumbComponent
          emitSchema={false}
          items={[
            { label: "Home", href: "/" },
            { label: "Tools", href: "/tools" },
            { label: breadcrumbLabel },
          ]}
        />
        {children}
      </div>
      {after}
    </div>
  )
}

/**
 * Breaks a two-column form/result grid out of the 5xl column to the max-w-6xl
 * content width at 1200px and up, while the hero and footer stay on the 5xl
 * edge. Narrower viewports have no side margin to spare, so it stays in the
 * column.
 */
export function ToolWideRegion({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("min-[1200px]:-mx-16", className)}>{children}</div>
}
